import { Preferences, KeysResult } from '@capacitor/preferences';
import { CachedItem } from '@/shared/interfaces/api';
import { DateTime } from 'luxon';

import { ValuesEnum } from '@/enums/values.enum';

const get = async <T = any>(key: string, notUsed = false): Promise<T> => {
	try {
		const item = await getCachedItem(key);

		return item?.value;
	} catch (err: any) {
		return err;
	}
};

const getCachedItem = async <T = any>(key: string): Promise<CachedItem<T> | null> => {
	try {
		const data = await Preferences.get({ key });

		if (!data) {
			return null;
		}

		const item = parse(data.value);

		if (!item) {
			return null;
		}

		if (isExpired(item) || (await isUnauthenticatedData(item))) {
			await remove(key);

			return null;
		}

		return item;
	} catch (err: any) {
		return err;
	}
};

const getCurrentUserId = async (): Promise<number | undefined> => {
	const data = await Preferences.get({ key: ValuesEnum.StoreProfile });

	if (!data) {
		return undefined;
	}

	const item = parse(data.value);

	if (!item) {
		return undefined;
	}

	return item?.value?.id;
};

/**
 * Helper function that will check if the current user can read the cached entry or not
 *
 * @param item cached entry
 * @returns boolean that indicates if user has the rights to read cache entry
 */
const isUnauthenticatedData = async (item: CachedItem<any>): Promise<boolean | number | undefined> => {
	if (!item.userID) {
		return false;
	}

	const currentUserID = await getCurrentUserId();

	return currentUserID && item.userID !== currentUserID;
};

/**
 * Helper function that will check if the cached entry is expired or not
 *
 * @param item cached entry
 * @returns boolean that indicates if cachd entry has expired or not
 */
const isExpired = (item: CachedItem<any>): boolean => {
	if (!item?.expiryDate) {
		return false;
	}

	const now = DateTime.now();
	const storedTime = DateTime.fromMillis(item.expiryDate);

	return now >= storedTime;
};

const remove = async (key: string): Promise<void> => {
	try {
		const data = await Preferences.remove({
			key,
		});
		return data;
	} catch (err: any) {
		console.error('REMOVE', err);
		return err;
	}
};

const parse = (value: any) => {
	try {
		return JSON.parse(value);
	} catch (e) {
		return value;
	}
};

/**
 * Checks if the error is a QuotaExceededError
 */
const isQuotaExceededError = (err: any): boolean => {
	return err instanceof DOMException && (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED' || err.code === 22 || err.code === 1014);
};

/**
 * Calculates the approximate size of the data in bytes
 */
const calculateDataSize = (data: any): number => {
	try {
		return new Blob([JSON.stringify(data)]).size;
	} catch {
		return 0;
	}
};

/**
 * Adds/sets entry in cache
 *
 * @param key Unique identifier of cached entry
 * @param value Data that has to be cached
 * @param isUserRelatedData Id that indicates that this kind of data is related to a specific user
 * @param expiryInSeconds Time to live
 *
 * @param notUsed
 */
export const set = async <T = any>(key: string, value: T, isUserRelatedData = false, expiryInSeconds?: number, notUsed = false): Promise<void> => {
	try {
		let item: CachedItem<T>;
		let userID: number | undefined;

		item = {
			value,
		};

		if (expiryInSeconds) {
			item = {
				...item,
				updatedAt: DateTime.now().valueOf(),
				expiryDate: DateTime.now().plus({ seconds: expiryInSeconds }).valueOf(),
			};
		}

		if (isUserRelatedData) {
			userID = await getCurrentUserId();
			item = {
				...item,
				userID,
			};
		}

		const confirmation = await Preferences.set({
			key,
			value: JSON.stringify(item),
		});

		return confirmation;
	} catch (err: any) {
		if (isQuotaExceededError(err)) {
			const dataSize = calculateDataSize(value);
			console.warn(`[Storage] QuotaExceededError: Failed to store '${key}' (size: ${(dataSize / 1024).toFixed(2)}KB). ` + `Consider clearing old cache entries or reducing data size.`);

			// Try to dynamically import and trigger cleanup
			try {
				const { default: useStorageCleanup } = await import('./useStorageCleanup');
				const { cleanupByPriority } = useStorageCleanup();
				// Try to free 5MB of space aggressively
				await cleanupByPriority(5 * 1024 * 1024);

				// Retry once after cleanup
				const confirmation = await Preferences.set({
					key,
					value: JSON.stringify({
						value,
						...(expiryInSeconds && {
							updatedAt: DateTime.now().valueOf(),
							expiryDate: DateTime.now().plus({ seconds: expiryInSeconds }).valueOf(),
						}),
						...(isUserRelatedData && { userID: await getCurrentUserId() }),
					}),
				});
				console.log(`[Storage] Successfully stored '${key}' after cleanup`);
				return confirmation;
			} catch (retryErr: any) {
				console.error(`[Storage] Failed to store '${key}' even after cleanup:`, retryErr);
				// Silently fail - app will continue without cache
			}
		} else {
			console.error('[Storage] SET error:', err);
		}
		return err;
	}
};

const useStorageService = () => {
	return {
		get,
		remove,
		set,
	};
};

export default useStorageService;
